import type { UserGroupFleetViewCarsListApiData, UserGroupFleetViewSummaryApiData } from '@navio/nsdk';
import { filterUnitFor, type SharedQuery } from 'noddi-ui-web';
import { TireWearInfoContent } from '../TireInfo/TireWearInfoContent';
import { fleetTreadDepthStatus, TIRE_SEASON } from './constants';
import type { FleetViewFilterTranslations } from './fleetViewTranslations';

/** Params the list and the summary charts both take: the charts count the cars the list shows. */
export type FleetViewFilterInput = SharedQuery<
  Omit<NonNullable<UserGroupFleetViewCarsListApiData['query']>, 'pageIndex' | 'pageSize' | 'ordering' | 'search'>,
  NonNullable<UserGroupFleetViewSummaryApiData['query']>
>;

export const fleetViewFilterUnit = filterUnitFor<FleetViewFilterInput>();

/**
 * The filters the summary charts read and write: the charts set them by clicking a slice, and their counts take
 * them, so the list and the charts never disagree on what they filter.
 */
export const fleetViewSummaryFilterSpecs = (t: FleetViewFilterTranslations) => {
  const tireWearStatusOptions = [
    { label: t.goodCondition, value: fleetTreadDepthStatus.ABOVE_RECOMMENDED },
    { label: t.shouldBeReplaced, value: fleetTreadDepthStatus.SOON_WORN_OUT },
    { label: t.mustBeReplaced, value: fleetTreadDepthStatus.WORN_OUT },
    { label: t.noMeasurements, value: fleetTreadDepthStatus.NO_MEASUREMENTS }
  ];

  return {
    inStorageSeason: fleetViewFilterUnit.multiSelect('inStorageSeason', {
      title: t.wheelSetInStorage,
      description: t.wheelSetInStorageDescription,
      options: [
        { label: t.winter, value: TIRE_SEASON.WINTER },
        { label: t.summer, value: TIRE_SEASON.SUMMER },
        { label: t.none, value: TIRE_SEASON.NONE }
      ],
      exclude: true
    }),
    tireWearStatusSummer: fleetViewFilterUnit.multiSelect('tireWearStatusSummer', {
      title: t.summerTireCondition,
      description: (
        <TireWearInfoContent
          season='summer'
          translations={
            t.tireWearInfo
              ? {
                  ...t.tireWearInfo,
                  title: t.tireWearInfo.summerTitle,
                  regulationsDescription: t.tireWearInfo.summerRegulationsDescription
                }
              : undefined
          }
        />
      ),
      options: tireWearStatusOptions,
      exclude: true
    }),
    tireWearStatusWinter: fleetViewFilterUnit.multiSelect('tireWearStatusWinter', {
      title: t.winterTireCondition,
      description: (
        <TireWearInfoContent
          season='winter'
          translations={
            t.tireWearInfo
              ? {
                  ...t.tireWearInfo,
                  title: t.tireWearInfo.winterTitle,
                  regulationsDescription: t.tireWearInfo.winterRegulationsDescription
                }
              : undefined
          }
        />
      ),
      options: tireWearStatusOptions,
      exclude: true
    }),
    hasBookingItemUpcoming: fleetViewFilterUnit.boolean('hasBookingItemUpcoming', {
      title: t.hasUpcomingBooking,
      description: t.hasUpcomingBookingDescription
    }),
    upcomingBookingDate: fleetViewFilterUnit.dateRangeOf(
      { from: 'upcomingBookingDateFrom', to: 'upcomingBookingDateTo' },
      { title: t.upcomingBookingDateRange, description: t.upcomingBookingDateRangeDescription, exclude: true }
    ),
    lastBookingDate: fleetViewFilterUnit.dateRangeOf(
      { from: 'lastBookingDateFrom', to: 'lastBookingDateTo' },
      { title: t.lastBookingDateRange, description: t.lastBookingDateRangeDescription, exclude: true }
    )
  };
};
